<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1080"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="wage-ledger-convert-dialog">
      <div class="wage-ledger-convert-dialog__header">
        <div class="header-icon">
          <v-icon size="18" color="white">mdi-swap-horizontal</v-icon>
        </div>
        <h2 class="header-title">임금대장 치환</h2>
        <span class="header-badge header-badge--test">테스트</span>
        <span class="header-badge">{{ wageTypeLabel }}</span>
        <button
          type="button"
          class="wage-ledger-convert-dialog__close"
          :disabled="busy"
          aria-label="닫기"
          @click="close"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="wage-ledger-convert-dialog__body">
        <p class="convert-intro">
          제목 줄, 병합, 두세 줄 헤더, 같은 항목명이 근로자·기관에 두 번 있는 임금대장도
          열 이름을 읽어서 <strong>카이온 임금대장 양식</strong>으로 바꿉니다.
          DB에는 저장하지 않으며, 변환 결과 엑셀을 내려받을 수 있습니다.
        </p>

        <div class="convert-steps">
          <span class="step-chip" :class="stepClass(1)">1. 엑셀 선택</span>
          <span class="step-chip" :class="stepClass(2)">2. 컬럼 매핑</span>
          <span class="step-chip" :class="stepClass(3)">3. 미리보기·다운로드</span>
        </div>

        <div class="convert-actions">
          <label class="convert-upload-label">
            <input
              ref="fileInputRef"
              type="file"
              accept=".xlsx,.xls"
              class="convert-upload-input"
              :disabled="busy"
              @change="onFileSelected"
            />
            <v-btn variant="flat" class="btn-search" size="small" tag="span" :disabled="busy">
              <v-icon size="14">mdi-upload</v-icon>
              엑셀 선택
            </v-btn>
          </label>
          <span v-if="selectedFileName" class="convert-file-name">{{ selectedFileName }}</span>
          <v-btn
            v-if="rawRows.length"
            variant="outlined"
            size="small"
            class="btn-refresh"
            :disabled="busy"
            @click="reSuggestMapping"
          >
            <v-icon size="14">mdi-auto-fix</v-icon>
            자동매핑 다시
          </v-btn>
        </div>

        <div v-if="sheetNames.length" class="convert-meta-row">
          <div class="meta-item">
            <span class="meta-label">시트</span>
            <select v-model="selectedSheet" class="meta-select" @change="onSheetChange">
              <option v-for="name in sheetNames" :key="name" :value="name">{{ name }}</option>
            </select>
          </div>
          <div class="meta-item">
            <span class="meta-label">헤더 행</span>
            <select v-model.number="headerRowIndex" class="meta-select" @change="onHeaderRowChange">
              <option v-for="opt in headerRowOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>

        <p v-if="recognizeNotes.length" class="convert-notes">
          {{ recognizeNotes.join(' ') }}
        </p>

        <div v-if="coverage" class="convert-summary">
          <span>매핑 {{ coverage.mapped }}/{{ coverage.total }}</span>
          <span :class="coverage.requiredOk ? 'ok' : 'err'">
            필수(성명) {{ coverage.requiredOk ? 'OK' : '미매핑' }}
          </span>
          <span v-if="convertedRows.length" class="ok">변환 {{ convertedRows.length }}명</span>
          <span v-if="errorRowCount" class="err">오류 {{ errorRowCount }}건</span>
        </div>

        <div v-if="sourceOptions.length" class="convert-map-wrap">
          <table class="convert-map-table">
            <thead>
              <tr>
                <th>카이온 항목</th>
                <th>원본 열</th>
                <th>샘플</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="col in columns"
                :key="col.key"
                :class="{
                  'row-mapped': mapping[col.key] != null,
                  'row-required-miss': col.required && mapping[col.key] == null,
                }"
              >
                <td>
                  {{ col.header }}
                  <span v-if="col.required" class="req-mark">*</span>
                </td>
                <td>
                  <select
                    class="map-select"
                    :value="mapping[col.key] == null ? '' : String(mapping[col.key])"
                    @change="onMappingChange(col.key, ($event.target as HTMLSelectElement).value)"
                  >
                    <option value="">— 매핑 안 함 —</option>
                    <option
                      v-for="opt in sourceOptions"
                      :key="`${col.key}-${opt.index}`"
                      :value="String(opt.index)"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </td>
                <td class="sample-cell" :title="sampleFor(col.key)">{{ sampleFor(col.key) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="convertedRows.length" class="convert-preview-wrap">
          <table class="convert-preview-table">
            <thead>
              <tr>
                <th>행</th>
                <th>성명</th>
                <th>생년월일</th>
                <th>직종</th>
                <th>기본급</th>
                <th>과세합계</th>
                <th>상태</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in previewDisplayRows"
                :key="row.rowNum"
                :class="{ 'row-error': row.errors.length }"
              >
                <td>{{ row.rowNum }}</td>
                <td>{{ row.name }}</td>
                <td>{{ previewBirth(row) || '-' }}</td>
                <td>{{ previewJob(row) || '-' }}</td>
                <td class="num">{{ formatNum(previewBasic(row)) }}</td>
                <td class="num">{{ formatNum(previewWageTotal(row)) }}</td>
                <td>{{ row.errors.length ? row.errors.join(', ') : 'OK' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else-if="selectedFileName && !busy" class="convert-empty">
          성명 열이 매핑되면 미리보기가 표시됩니다.
        </p>
      </div>

      <div class="wage-ledger-convert-dialog__footer">
        <v-btn variant="text" class="btn-close" :disabled="busy" @click="close">닫기</v-btn>
        <v-spacer />
        <v-btn
          variant="flat"
          class="btn-save"
          :disabled="busy || !canDownload"
          @click="downloadConverted"
        >
          <v-icon size="14">mdi-download</v-icon>
          카이온 양식으로 다운로드
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import * as XLSX from 'xlsx';
import {
  type ColumnMapping,
  type ConvertedRow,
  type WageConvertType,
  analyzeHeaders,
  applyWageColumnDictionary,
  buildSourceColumnOptions,
  convertRows,
  expandMergedCells,
  getConvertColumns,
  mappingCoverage,
  pivotVerticalLedger,
  suggestColumnMapping,
  type WageColumnDictEntry,
} from '@/utils/wageLedgerConvert';
import { commonWageApi } from '@/services/api';

const props = defineProps<{
  modelValue: boolean;
  wageType: WageConvertType;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const fileInputRef = ref<HTMLInputElement | null>(null);
const selectedFileName = ref('');
const busy = ref(false);
const workbookRef = ref<XLSX.WorkBook | null>(null);
const sheetNames = ref<string[]>([]);
const selectedSheet = ref('');
const rawRows = ref<unknown[][]>([]);
const headerRowIndex = ref(0);
const dataStartIndex = ref(1);
const composedHeaders = ref<string[]>([]);
const sheetNotes = ref<string[]>([]);
const analyzeNotes = ref<string[]>([]);
const dictNote = ref('');
const headerPinned = ref(false);
let dictLoading: Promise<void> | null = null;
const mapping = reactive<ColumnMapping>({});
const convertedRows = ref<ConvertedRow[]>([]);

const recognizeNotes = computed(() =>
  [dictNote.value, ...sheetNotes.value, ...analyzeNotes.value].filter(Boolean)
);

const ensureColumnDict = () => {
  if (!dictLoading) {
    dictLoading = commonWageApi
      .getColumnDict()
      .then((response) => {
        const list = response.data?.data as WageColumnDictEntry[] | undefined;
        if (response.data?.success && Array.isArray(list) && list.length) {
          applyWageColumnDictionary(list);
          dictNote.value = `컬럼 사전 ${list.length}개 항목으로 헤더를 맞춥니다.`;
        }
      })
      .catch(() => {
        dictNote.value = '';
        dictLoading = null;
      });
  }
  return dictLoading;
};

const columns = computed(() => getConvertColumns(props.wageType));
const wageTypeLabel = computed(() => (props.wageType === 'home' ? '방문요양' : '요양시설'));

const sourceOptions = computed(() => buildSourceColumnOptions(composedHeaders.value));
const dataRows = computed(() => rawRows.value.slice(dataStartIndex.value));

const coverage = computed(() => {
  if (!rawRows.value.length) return null;
  return mappingCoverage(mapping, columns.value);
});

const errorRowCount = computed(() => convertedRows.value.filter((r) => r.errors.length).length);
const canDownload = computed(() => convertedRows.value.length > 0 && !!coverage.value?.requiredOk);

const previewDisplayRows = computed(() => convertedRows.value.slice(0, 80));

const headerRowOptions = computed(() => {
  const limit = Math.min(rawRows.value.length, 25);
  const opts = [];
  for (let i = 0; i < limit; i++) {
    const cells = (rawRows.value[i] || [])
      .slice(0, 6)
      .map((c) => String(c ?? '').trim())
      .filter(Boolean)
      .join(' / ');
    opts.push({
      value: i,
      label: `${i + 1}행${cells ? ` — ${cells.slice(0, 48)}` : ''}`,
    });
  }
  return opts;
});

const currentStep = computed(() => {
  if (!rawRows.value.length) return 1;
  if (!convertedRows.value.length) return 2;
  return 3;
});

const stepClass = (step: number) => {
  if (currentStep.value === step) return 'step-chip--active';
  if (currentStep.value > step) return 'step-chip--done';
  return '';
};

const nameKey = computed(() => (props.wageType === 'facility' ? 'cwhd_name' : 'name'));
const birthKey = computed(() => (props.wageType === 'facility' ? 'cwhd_birth' : 'birth'));
const jobKey = computed(() => (props.wageType === 'facility' ? 'ce_position' : 'job'));
const basicKey = computed(() => (props.wageType === 'facility' ? 'cwhd_basic' : 'basic'));
const wageTotalKey = computed(() =>
  props.wageType === 'facility' ? 'cwhd_wage_total' : 'wageTotal'
);

const previewBirth = (row: ConvertedRow) => String(row.values[birthKey.value] ?? '');
const previewJob = (row: ConvertedRow) => String(row.values[jobKey.value] ?? '');
const previewBasic = (row: ConvertedRow) => Number(row.values[basicKey.value]) || 0;
const previewWageTotal = (row: ConvertedRow) => Number(row.values[wageTotalKey.value]) || 0;

const formatNum = (n: number) => {
  if (!n) return '-';
  return n.toLocaleString();
};

const sampleFor = (key: string): string => {
  const idx = mapping[key];
  if (idx == null) return '';
  const first = dataRows.value.find((r) => {
    const v = r?.[idx as number];
    return v != null && String(v).trim() !== '';
  });
  if (!first) return '';
  return String(first[idx as number] ?? '').slice(0, 40);
};

const excelSerialToDate = (n: number): string => {
  try {
    const d = XLSX.SSF.parse_date_code(n);
    if (!d) return '';
    return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
  } catch {
    return '';
  }
};

const clearMapping = () => {
  Object.keys(mapping).forEach((k) => delete mapping[k]);
};

const applyMappingObject = (next: ColumnMapping) => {
  clearMapping();
  for (const [k, v] of Object.entries(next)) {
    mapping[k] = v;
  }
};

const rebuildConverted = () => {
  if (!rawRows.value.length || mapping[nameKey.value] == null) {
    convertedRows.value = [];
    return;
  }
  convertedRows.value = convertRows(
    dataRows.value,
    dataStartIndex.value,
    { ...mapping },
    columns.value,
    { excelSerialToDate }
  );
};

const reSuggestMapping = () => {
  const notes: string[] = [];
  const suggested = suggestColumnMapping(
    composedHeaders.value,
    columns.value,
    dataRows.value,
    notes
  );
  analyzeNotes.value = [...analyzeHeaders(
    rawRows.value,
    headerPinned.value ? headerRowIndex.value : null
  ).notes, ...notes];
  applyMappingObject(suggested);
  rebuildConverted();
};

const runAnalyze = () => {
  const analysis = analyzeHeaders(
    rawRows.value,
    headerPinned.value ? headerRowIndex.value : null
  );
  headerRowIndex.value = analysis.headerRowIndex;
  dataStartIndex.value = analysis.dataStartIndex;
  composedHeaders.value = analysis.labels;
  const mapNotes: string[] = [];
  const suggested = suggestColumnMapping(
    analysis.labels,
    columns.value,
    rawRows.value.slice(analysis.dataStartIndex),
    mapNotes
  );
  analyzeNotes.value = [...analysis.notes, ...mapNotes];
  applyMappingObject(suggested);
  rebuildConverted();
};

const loadSheet = (sheetName: string) => {
  const wb = workbookRef.value;
  if (!wb || !sheetName) return;
  const sheet = wb.Sheets[sheetName];
  if (!sheet) return;
  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: true,
  }) as unknown[][];
  const expanded = expandMergedCells(matrix, sheet['!merges'] || []);
  const notes: string[] = [];
  if ((sheet['!merges'] || []).length) notes.push('병합된 칸을 펼쳐 읽었습니다.');
  const pivoted = pivotVerticalLedger(expanded);
  if (pivoted.pivoted) notes.push('세로로 쌓인 항목을 임금대장 행으로 돌렸습니다.');
  sheetNotes.value = notes;
  rawRows.value = pivoted.rows;
  headerPinned.value = false;
  runAnalyze();
};

const onSheetChange = () => {
  loadSheet(selectedSheet.value);
};

const onHeaderRowChange = () => {
  headerPinned.value = true;
  runAnalyze();
};

const onMappingChange = (key: string, value: string) => {
  mapping[key] = value === '' ? null : Number(value);
  rebuildConverted();
};

const resetState = () => {
  selectedFileName.value = '';
  workbookRef.value = null;
  sheetNames.value = [];
  selectedSheet.value = '';
  rawRows.value = [];
  headerRowIndex.value = 0;
  dataStartIndex.value = 1;
  composedHeaders.value = [];
  sheetNotes.value = [];
  analyzeNotes.value = [];
  headerPinned.value = false;
  clearMapping();
  convertedRows.value = [];
  if (fileInputRef.value) fileInputRef.value.value = '';
};

watch(
  () => props.modelValue,
  (open) => {
    if (!open) resetState();
    else ensureColumnDict();
  }
);

watch(
  () => props.wageType,
  () => {
    if (rawRows.value.length) reSuggestMapping();
  }
);

const close = () => {
  emit('update:modelValue', false);
};

const onFileSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!['xlsx', 'xls'].includes(ext || '')) {
    alert('xlsx 또는 xls 파일만 가능합니다.');
    return;
  }

  try {
    busy.value = true;
    selectedFileName.value = file.name;
    await ensureColumnDict();
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(new Uint8Array(buf), { type: 'array', cellDates: true });
    workbookRef.value = wb;
    sheetNames.value = wb.SheetNames.slice();
    selectedSheet.value = wb.SheetNames[0] || '';
    loadSheet(selectedSheet.value);
  } catch (e: any) {
    console.error(e);
    alert(`엑셀을 읽는 중 오류: ${e?.message || '알 수 없는 오류'}`);
    resetState();
  } finally {
    busy.value = false;
  }
};

const downloadConverted = () => {
  if (!canDownload.value) return;
  const cols = columns.value;
  const headers = cols.map((c) => c.header);
  const aoa: (string | number)[][] = [headers];
  for (const row of convertedRows.value) {
    aoa.push(cols.map((c) => row.values[c.key] ?? (c.type === 'number' ? 0 : '')));
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '임금마이그레이션');
  const label = props.wageType === 'home' ? '방문요양' : '요양시설';
  XLSX.writeFile(wb, `카이온_임금대장_치환_${label}.xlsx`);
};
</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/components/wage-ledger-convert-modal';
</style>
